import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message } from "antd";
import { LoadingStatusCode } from "../../../../lib/types/LoadingStatusCode";
import { catchApiError } from "../../../../lib/utils/catchApiError";
import type { IPoliciesListSearch } from "../../types/IPoliciesListSearch";
import {
  createPolicyRoute,
  fetchOfferByIdRoute,
  fetchPolicyVersionsRoute,
  getPoliciesDicRoute,
  searchPoliciesRoute,
  searchPolicyIdRoute,
  updateCloseDatePolicyRoute,
  updatePolicyRoute,
} from "./policiesRoutes";
import type { IPolicy } from "../../types/IPolicy";
import type { DictionaryDto } from "../../../../lib/interfaces";
import { offeringGroupIdSearchRoute } from "./offeringGroupsRoutes";
import { IOfferingGroup } from "../../types/IOfferingGroup";
import { Offer } from "../../../../lib/types";

interface InitialState {
  policiesTypesDic: DictionaryDto[];
  policiesTypesDicLoading: LoadingStatusCode;
  policiesActionsDic: DictionaryDto[];
  policiesActionsDicLoading: LoadingStatusCode;
  policiesContextsDic: DictionaryDto[];
  policiesContextsDicLoading: LoadingStatusCode;
  policiesStatusDic: DictionaryDto[];
  policiesStatusDicLoading: LoadingStatusCode;
  policiesEntityObjectTypeDic: DictionaryDto[];
  policiesEntityObjectTypeLoading: LoadingStatusCode;
  policiesList: IPolicy[];
  policiesListPagination: {
    current: number;
    total: number;
  };
  policiesListLoading: LoadingStatusCode;
  policiesListTotalElements: number;
  policyVersions: IPolicy[];
  policyVersionsLoading: LoadingStatusCode;
  policyOfferingGroup?: IOfferingGroup;
  policyOfferingGroupLoading: LoadingStatusCode;
  policy?: IPolicy;
  policyLoading: LoadingStatusCode;
  offerPolicyView?: Offer;
  offerPolicyViewLoading: LoadingStatusCode;
  updatePolicyLoading: LoadingStatusCode;
  updateCloseDatePolicyLoading: LoadingStatusCode;
  createPolicyLoading: LoadingStatusCode;
}

const initialState: InitialState = {
  policiesTypesDic: [],
  policiesTypesDicLoading: "idle",
  policiesActionsDic: [],
  policiesActionsDicLoading: "idle",
  policiesContextsDic: [],
  policiesContextsDicLoading: "idle",
  policiesStatusDic: [],
  policiesStatusDicLoading: "idle",
  policiesEntityObjectTypeDic: [],
  policiesEntityObjectTypeLoading: "idle",
  policiesList: [],
  policiesListPagination: {
    current: 0,
    total: 0,
  },
  policiesListLoading: "idle",
  policiesListTotalElements: 0,
  policyVersions: [],
  policyVersionsLoading: "idle",
  policyOfferingGroupLoading: "idle",
  policyLoading: "idle",
  offerPolicyViewLoading: "idle",
  updatePolicyLoading: "idle",
  updateCloseDatePolicyLoading: "idle",
  createPolicyLoading: "idle",
};

// Получение справочника типов политик
export const fetchPoliciesTypesDic = createAsyncThunk(
  "policies/fetchPoliciesTypesDic",
  async () => {
    try {
      const response = axios.get(getPoliciesDicRoute("policy_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка политик. Попробуйте позже",
      });
    }
  }
);

// Получение справочника действий
export const fetchPoliciesActionsDic = createAsyncThunk(
  "policies/fetchPoliciesActionsDic",
  async () => {
    try {
      const response = axios.get(getPoliciesDicRoute("policy_action"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка действий. Попробуйте позже",
      });
    }
  }
);

// Получение справочника контекстов
export const fetchPoliciesContextsDic = createAsyncThunk(
  "policies/fetchPoliciesContextsDic",
  async () => {
    try {
      const response = axios.get(getPoliciesDicRoute("policy_context"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка контекстов. Попробуйте позже",
      });
    }
  }
);

// Получение справочника статусов
export const fetchPoliciesStatusDic = createAsyncThunk(
  "policies/fetchPoliciesStatusDic",
  async () => {
    try {
      const response = axios.get(getPoliciesDicRoute("policy_status"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка статусов. Попробуйте позже",
      });
    }
  }
);

// Получение справочника энтити
export const fetchPoliciesEntityObjectTypeDic = createAsyncThunk(
  "policies/fetchPoliciesEntityObjectTypeDic",
  async () => {
    try {
      const response = axios.get(getPoliciesDicRoute("entity_object_type"));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки списка типов объекта. Попробуйте позже",
      });
    }
  }
);

// Поиск политик
export const searchPolicies = createAsyncThunk(
  "policies/searchPolicies",
  async (searchParams: IPoliciesListSearch<string>) => {
    try {
      const response = axios.get(searchPoliciesRoute(searchParams));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки политик. Попробуйте позже",
      });
    }
  }
);

// Получение версий политики
export const fetchPolicyVersions = createAsyncThunk(
  "policies/fetchPolicyVersions",
  async (policyId: number) => {
    try {
      const response = axios.get(fetchPolicyVersionsRoute(policyId));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки политик. Попробуйте позже",
      });
    }
  }
);

// Поиск группы предложения для параметров создания
export const searchOfferingGroupForPolicyCreate = createAsyncThunk(
  "policies/searchOfferingGroupForPolicyCreate",
  async (offeringGroupid: number) => {
    try {
      const response = axios.get(offeringGroupIdSearchRoute(offeringGroupid));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки загрузки политик. Попробуйте позже",
      });
    }
  }
);

// Создание политики
export const createPolicy = createAsyncThunk("policies/createPolicy", async (params: any) => {
  try {
    const response = axios.post(createPolicyRoute(), params);
    const { data } = await response;

    message.success("Политика успешно создана");

    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка создания политики. Попробуйте позже",
    });
  }
});

// Получение информации о политике
export const searchPolicy = createAsyncThunk("policies/searchPolicy", async (policyId: number) => {
  try {
    const response = axios.get(searchPolicyIdRoute(policyId));
    const { data } = await response;
    return data;
  } catch (e) {
    return catchApiError({
      e,
      title: "Ошибка загрузки информации о политике. Попробуйте позже",
    });
  }
});

// Получение предложения по id
export const fetchOfferById = createAsyncThunk<Offer, { id: number; onDate: string }>(
  "policies/fetchOfferById",
  async (params) => {
    try {
      const response = axios.get<Offer>(fetchOfferByIdRoute(params));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки предложения. Попробуйте позже",
      });
    }
  }
);

// Обновление политики
export const updatePolicy = createAsyncThunk(
  "policies/updatePolicy",
  async (
    {
      params,
      body,
    }: {
      params: {
        policyId: number;
        params: { fromDate?: string; force: boolean };
      };
      body: any;
    },
    { dispatch }
  ) => {
    try {
      await axios.patch(updatePolicyRoute(params), body);

      message.success("Успешно обновлено");

      return Promise.all([
        dispatch(searchPolicy(params.policyId)),
        dispatch(fetchPolicyVersions(params.policyId)),
      ]);
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления. Попробуйте позже",
      });
    }
  }
);

// Обновление даты закрытия политики
export const updateCloseDatePolicy = createAsyncThunk(
  "policies/updateCloseDatePolicy",
  async (
    params: {
      policyId: number;
      onDate?: string;
    },
    { dispatch }
  ) => {
    try {
      await axios.delete(updateCloseDatePolicyRoute(params));

      message.success("Дата закрытия успешно обновлена");

      return Promise.all([
        dispatch(searchPolicy(params.policyId)),
        dispatch(fetchPolicyVersions(params.policyId)),
      ]);
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка обновления даты закрытия. Попробуйте позже",
      });
    }
  }
);

export const policiesSlice = createSlice({
  name: "policies",
  initialState,
  reducers: {
    cleanPoliciesList: (state) => {
      state.policiesListLoading = "idle";
      state.policiesList = [];
      state.policiesListTotalElements = 0;
      state.policiesListPagination = {
        current: 0,
        total: 0,
      };
    },
    cleanPolicy: (state) => {
      state.policyLoading = "idle";
      state.policy = undefined;
    },
    cleanPolicyVersions: (state) => {
      state.policyVersionsLoading = "idle";
      state.policyVersions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPoliciesTypesDic.pending, (state) => {
        state.policiesTypesDicLoading = "pending";
      })
      .addCase(fetchPoliciesTypesDic.rejected, (state) => {
        state.policiesTypesDicLoading = "rejected";
        state.policiesTypesDic = [];
      })
      .addCase(fetchPoliciesTypesDic.fulfilled, (state, { payload }) => {
        state.policiesTypesDicLoading = "fulfilled";
        state.policiesTypesDic = payload;
      })
      .addCase(fetchPoliciesActionsDic.pending, (state) => {
        state.policiesActionsDicLoading = "pending";
      })
      .addCase(fetchPoliciesActionsDic.rejected, (state) => {
        state.policiesActionsDicLoading = "rejected";
        state.policiesActionsDic = [];
      })
      .addCase(fetchPoliciesActionsDic.fulfilled, (state, { payload }) => {
        state.policiesActionsDicLoading = "fulfilled";
        state.policiesActionsDic = payload;
      })
      .addCase(fetchPoliciesContextsDic.pending, (state) => {
        state.policiesContextsDicLoading = "pending";
      })
      .addCase(fetchPoliciesContextsDic.rejected, (state) => {
        state.policiesContextsDicLoading = "rejected";
        state.policiesContextsDic = [];
      })
      .addCase(fetchPoliciesContextsDic.fulfilled, (state, { payload }) => {
        state.policiesContextsDicLoading = "fulfilled";
        state.policiesContextsDic = payload;
      })
      .addCase(fetchPoliciesStatusDic.pending, (state) => {
        state.policiesStatusDicLoading = "pending";
      })
      .addCase(fetchPoliciesStatusDic.rejected, (state) => {
        state.policiesStatusDicLoading = "rejected";
        state.policiesStatusDic = [];
      })
      .addCase(fetchPoliciesStatusDic.fulfilled, (state, { payload }) => {
        state.policiesStatusDicLoading = "fulfilled";
        state.policiesStatusDic = payload;
      })
      .addCase(fetchPoliciesEntityObjectTypeDic.pending, (state) => {
        state.policiesEntityObjectTypeLoading = "pending";
      })
      .addCase(fetchPoliciesEntityObjectTypeDic.rejected, (state) => {
        state.policiesEntityObjectTypeLoading = "rejected";
        state.policiesEntityObjectTypeDic = [];
      })
      .addCase(fetchPoliciesEntityObjectTypeDic.fulfilled, (state, { payload }) => {
        state.policiesEntityObjectTypeLoading = "fulfilled";
        state.policiesEntityObjectTypeDic = payload;
      })
      .addCase(searchPolicies.pending, (state) => {
        state.policiesListLoading = "pending";
      })
      .addCase(searchPolicies.rejected, (state) => {
        state.policiesListLoading = "rejected";
        state.policiesList = [];
        state.policiesListTotalElements = 0;
        state.policiesListPagination = {
          current: 0,
          total: 0,
        };
      })
      .addCase(searchPolicies.fulfilled, (state, { payload }) => {
        state.policiesListLoading = "fulfilled";
        state.policiesList = payload.content;
        state.policiesListTotalElements = payload.totalElements;
        state.policiesListPagination = {
          current: payload.number,
          total: payload.totalElements,
        };
      })
      .addCase(fetchPolicyVersions.pending, (state) => {
        state.policyVersionsLoading = "pending";
      })
      .addCase(fetchPolicyVersions.rejected, (state) => {
        state.policyVersionsLoading = "rejected";
        state.policyVersions = [];
      })
      .addCase(fetchPolicyVersions.fulfilled, (state, { payload }) => {
        state.policyVersionsLoading = "fulfilled";
        state.policyVersions = payload;
      })
      .addCase(searchOfferingGroupForPolicyCreate.pending, (state) => {
        state.policyOfferingGroupLoading = "pending";
      })
      .addCase(searchOfferingGroupForPolicyCreate.rejected, (state) => {
        state.policyOfferingGroupLoading = "rejected";
        state.policyOfferingGroup = undefined;
      })
      .addCase(searchOfferingGroupForPolicyCreate.fulfilled, (state, { payload }) => {
        state.policyOfferingGroupLoading = "fulfilled";
        state.policyOfferingGroup = payload;
      })
      .addCase(searchPolicy.pending, (state) => {
        state.policyLoading = "pending";
      })
      .addCase(searchPolicy.rejected, (state) => {
        state.policyLoading = "rejected";
        state.policy = undefined;
      })
      .addCase(searchPolicy.fulfilled, (state, { payload }) => {
        state.policyLoading = "fulfilled";
        state.policy = payload;
      })
      .addCase(fetchOfferById.pending, (state) => {
        state.offerPolicyViewLoading = "pending";
      })
      .addCase(fetchOfferById.rejected, (state) => {
        state.offerPolicyViewLoading = "rejected";
        state.offerPolicyView = undefined;
      })
      .addCase(fetchOfferById.fulfilled, (state, { payload }) => {
        state.offerPolicyViewLoading = "fulfilled";
        state.offerPolicyView = payload;
      })
      .addCase(updatePolicy.pending, (state) => {
        state.updatePolicyLoading = "pending";
      })
      .addCase(updatePolicy.rejected, (state) => {
        state.updatePolicyLoading = "rejected";
      })
      .addCase(updatePolicy.fulfilled, (state, { payload }) => {
        state.updatePolicyLoading = "fulfilled";
      })
      .addCase(updateCloseDatePolicy.pending, (state) => {
        state.updateCloseDatePolicyLoading = "pending";
      })
      .addCase(updateCloseDatePolicy.rejected, (state) => {
        state.updateCloseDatePolicyLoading = "rejected";
      })
      .addCase(updateCloseDatePolicy.fulfilled, (state, { payload }) => {
        state.updateCloseDatePolicyLoading = "fulfilled";
      })
      .addCase(createPolicy.pending, (state) => {
        state.createPolicyLoading = "pending";
      })
      .addCase(createPolicy.rejected, (state) => {
        state.createPolicyLoading = "rejected";
      })
      .addCase(createPolicy.fulfilled, (state, { payload }) => {
        state.createPolicyLoading = "fulfilled";
      });
  },
});

export const { reducer: policiesReducer, actions: policiesActions } = policiesSlice;
